import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Calendar } from '../components/ui/calendar';
import AppointmentCard from '../components/AppointmentCard';
import CreateAppointmentModal from '../components/CreateAppointmentModal';
import PatientProfileModal from '../components/PatientProfileModal';
import RescheduleModal from '../components/RescheduleModal';
import FollowUpModal from '../components/FollowUpModal';
import NotesModal from '../components/NotesModal';
import IssueTokenModal from '../components/IssueTokenModal';
import RecentFeedbackScoresSection from '../components/RecentFeedbackScoresSection';
import NewRegistrationsBox from '../components/NewRegistrationsBox';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Search, Filter, Monitor, Smartphone, IndianRupee, CreditCard, X, ClipboardList, Package as PackageIcon, RefreshCcw, PencilLine, CheckCircle2, Clock, PlayCircle, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

const Dashboard = ({ user, onLogout }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(() => {
    // Check if date is passed in URL
    const dateParam = searchParams.get('date');
    if (dateParam) {
      return new Date(dateParam + 'T00:00:00');
    }
    return new Date();
  });
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [bookPatient, setBookPatient] = useState(null);
  const [registrationsKey, setRegistrationsKey] = useState(0);
  const canSeeRegistrations = user?.role === 'Admin' || user?.role === 'FrontDeskExecutive';
  const openBookingFor = (patient) => { setBookPatient(patient); setShowCreateModal(true); };
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedAppointmentForReschedule, setSelectedAppointmentForReschedule] = useState(null);
  const [selectedAppointmentForFollowUp, setSelectedAppointmentForFollowUp] = useState(null);
  const [selectedAppointmentForNotes, setSelectedAppointmentForNotes] = useState(null);
  const [selectedAppointmentForIssueToken, setSelectedAppointmentForIssueToken] = useState(null);
  const [highlightedAppointmentId, setHighlightedAppointmentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [isMobileView, setIsMobileView] = useState(() => {
    // Load from localStorage, default to false (desktop view)
    return localStorage.getItem('dashboardMobileView') === 'true';
  });
  const [mobileActiveTab, setMobileActiveTab] = useState('time-based'); // 'time-based' or 'queue-based'
  const [duplicateAppointmentData, setDuplicateAppointmentData] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState({ totalsByMethod: {}, grandTotal: 0 });
  // Today's package activity (Admin mobile)
  const [pkgSummary, setPkgSummary] = useState({
    created: { count: 0, rows: [] },
    redeemed: { count: 0, rows: [] },
    edited: { count: 0, rows: [] },
  });
  const [pkgDrill, setPkgDrill] = useState(null); // 'created' | 'redeemed' | 'edited' | null
  // Today's job cards (Admin mobile)
  const [jobCards, setJobCards] = useState([]);
  const [jcDrill, setJcDrill] = useState(false); // open/close
  // Pharmacy daily sales total (Admin mobile)
  const [pharmacyDailyTotal, setPharmacyDailyTotal] = useState({ total: 0, count: 0 });
  
  const calendarClickTimeout = useRef(null);
  const isProcessingDateChange = useRef(false);

  // Handle URL params for highlighting
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    const dateParam = searchParams.get('date');
    
    if (highlightId) {
      // If date param exists and is different from current, update the date first
      if (dateParam) {
        const newDate = new Date(dateParam + 'T00:00:00');
        if (newDate.toDateString() !== selectedDate.toDateString()) {
          setSelectedDate(newDate);
        }
      }
      
      // Clear the URL params immediately
      searchParams.delete('highlight');
      searchParams.delete('date');
      setSearchParams(searchParams, { replace: true });
      
      // Set highlight after a delay to ensure data is loaded
      setTimeout(() => {
        setHighlightedAppointmentId(highlightId);
        
        // Scroll to highlighted element
        const highlightedElement = document.querySelector(`[data-appointment-id="${highlightId}"]`);
        if (highlightedElement) {
          highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a flash animation
          highlightedElement.classList.add('animate-pulse');
          setTimeout(() => {
            highlightedElement.classList.remove('animate-pulse');
          }, 2000);
        }
      }, 1500);
      
      // Remove highlight after 8 seconds
      setTimeout(() => {
        setHighlightedAppointmentId(null);
      }, 8000);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    fetchAppointments();
    fetchServices();
    if (user?.role === 'Admin') {
      fetchPaymentSummary();
      fetchPackageSummary();
      fetchJobCardsSummary();
      fetchPharmacyDailyTotal();
    }
  }, [selectedDate]);

  const fetchPharmacyDailyTotal = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await api.get('/pharmacy/sales-daily-total', { params: { date: dateStr } });
      setPharmacyDailyTotal({
        total: Number(res.data?.total || 0),
        count: Number(res.data?.count || 0),
      });
    } catch (err) {
      setPharmacyDailyTotal({ total: 0, count: 0 });
    }
  };

  const fetchJobCardsSummary = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await api.get('/job-cards', { params: { date: dateStr } });
      setJobCards(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch job cards:', err);
    }
  };

  const fetchPackageSummary = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await api.get('/packages-daily-summary', { params: { date: dateStr } });
      setPkgSummary({
        created: res.data?.created || { count: 0, rows: [] },
        redeemed: res.data?.redeemed || { count: 0, rows: [] },
        edited: res.data?.edited || { count: 0, rows: [] },
      });
    } catch (err) {
      console.error('Failed to fetch package summary:', err);
    }
  };

  const fetchPaymentSummary = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get(`/payments?date=${dateStr}`);
      const payments = response.data || [];
      
      const totalsByMethod = {};
      let grandTotal = 0;
      
      payments.filter(p => p.status === 'completed').forEach(p => {
        // Check if payment has splits for accurate breakdown
        if (p.payment_splits && p.payment_splits.length > 0) {
          p.payment_splits.forEach(split => {
            const methodName = split.payment_method_name || 'Unknown';
            if (!totalsByMethod[methodName]) {
              totalsByMethod[methodName] = 0;
            }
            totalsByMethod[methodName] += split.amount || 0;
          });
        } else {
          // Fallback to primary payment method
          const methodName = p.payment_method_name || 'Unknown';
          if (!totalsByMethod[methodName]) {
            totalsByMethod[methodName] = 0;
          }
          totalsByMethod[methodName] += p.amount;
        }
        grandTotal += p.amount;
      });
      
      setPaymentSummary({ totalsByMethod, grandTotal });
    } catch (error) {
      console.error('Failed to fetch payment summary:', error);
    }
  };

  const toggleMobileView = () => {
    const newValue = !isMobileView;
    setIsMobileView(newValue);
    localStorage.setItem('dashboardMobileView', newValue.toString());
  };

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      // Format date as YYYY-MM-DD in local timezone (not UTC)
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      console.log('Fetching appointments for date:', dateStr);
      const response = await api.get(`/appointments?date=${dateStr}`);
      console.log('Received appointments:', response.data.length);
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services');
    }
  };

  const handleCreateAppointment = async (appointmentData, forceCreate = false) => {
    try {
      const dataToSend = { ...appointmentData, force_create: forceCreate };
      const response = await api.post('/appointments', dataToSend);
      const newAppointmentId = response.data.id;
      toast.success('Appointment created successfully!');
      setShowCreateModal(false);
      setBookPatient(null);
      setRegistrationsKey((k) => k + 1);
      setDuplicateAppointmentData(null); // Clear duplicate data
      
      // Highlight and scroll to new appointment
      setHighlightedAppointmentId(newAppointmentId);
      await fetchAppointments();
      
      setTimeout(() => {
        const element = document.getElementById(`appointment-${newAppointmentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => setHighlightedAppointmentId(null), 3000);
      }, 100);
    } catch (error) {
      // Check if it's a duplicate appointment error (409 status code)
      if (error.response?.status === 409 && error.response?.data) {
        const errorData = error.response.data;
        // Store duplicate data and show custom dialog
        setDuplicateAppointmentData({
          message: typeof errorData === 'string' ? errorData : errorData.message,
          existingTime: errorData.existing_time,
          existingStatus: errorData.existing_status,
          originalData: appointmentData
        });
      } else {
        // Other errors
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to create appointment';
        toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      }
    }
  };

  const handleAction = async (action, appointmentId, data = {}) => {
    try {
      switch (action) {
        case 'checkin':
          await api.post(`/appointments/${appointmentId}/check-in`, {});
          toast.success('Patient checked in successfully');
          break;
        case 'undocheckin':
          await api.post(`/appointments/${appointmentId}/undo-check-in`, {});
          toast.success('Check-in undone successfully');
          break;
        case 'checkout':
          await api.post(`/appointments/${appointmentId}/check-out`, {});
          toast.success('Patient checked out successfully');
          break;
        case 'cancel':
          await api.put(`/appointments/${appointmentId}`, { status: 'cancelled' });
          toast.success('Appointment cancelled');
          break;
        case 'confirm':
          await api.put(`/appointments/${appointmentId}`, { status: 'confirmed' });
          toast.success('Appointment confirmed');
          break;
        case 'markpaid':
          await api.post(`/appointments/${appointmentId}/mark-paid`, {});
          toast.success('Payment marked successfully');
          break;
        case 'markpackage':
          await api.post(`/appointments/${appointmentId}/mark-package`, {});
          toast.success('Package marked successfully');
          break;
        case 'undopackage':
          await api.post(`/appointments/${appointmentId}/undo-package`, {});
          toast.success('Package undone successfully');
          break;
        case 'noshow':
          await api.post('/no-shows', {
            appointment_id: appointmentId,
            patient_id: data.patient_id
          });
          toast.success('Marked as no-show');
          break;
      }
      
      // Highlight the appointment and scroll to it after refresh
      setHighlightedAppointmentId(appointmentId);
      await fetchAppointments();
      
      // Scroll to the appointment after a short delay to ensure DOM is updated
      setTimeout(() => {
        const element = document.getElementById(`appointment-${appointmentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        // Remove highlight after 3 seconds
        setTimeout(() => {
          setHighlightedAppointmentId(null);
        }, 3000);
      }, 100);
    } catch (error) {
      console.error('Action error:', error.response?.data || error);
      const errorDetail = error.response?.data?.detail || error.response?.data?.message || error.message || 'Action failed';
      toast.error(typeof errorDetail === 'string' ? errorDetail : JSON.stringify(errorDetail));
    }
  };

  const handleSendDirections = async (appointmentId) => {
    try {
      await api.post(`/appointments/${appointmentId}/send-directions`);
      toast.success('Directions sent via WhatsApp');
    } catch (error) {
      toast.error('Failed to send directions');
    }
  };

  const handleViewPatient = (patientId) => {
    setSelectedPatient(patientId);
  };

  const handleReschedule = (appointment) => {
    setSelectedAppointmentForReschedule(appointment);
  };

  const handleFollowUp = (appointment) => {
    setSelectedAppointmentForFollowUp(appointment);
  };

  const handleAddNotes = (appointment) => {
    setSelectedAppointmentForNotes(appointment);
  };

  const handleIssueToken = (appointment) => {
    setSelectedAppointmentForIssueToken(appointment);
  };

  const handleIssueTokenSubmit = async (isComplimentary) => {
    try {
      const appointmentId = selectedAppointmentForIssueToken.id;
      await api.post(`/appointments/${appointmentId}/issue-token?is_complimentary=${isComplimentary}`, {});
      toast.success(`Token issued successfully${isComplimentary ? ' (Complimentary)' : ''}`);
      setSelectedAppointmentForIssueToken(null);
      
      // Highlight and scroll to appointment
      setHighlightedAppointmentId(appointmentId);
      await fetchAppointments();
      
      setTimeout(() => {
        const element = document.getElementById(`appointment-${appointmentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => setHighlightedAppointmentId(null), 3000);
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue token');
    }
  };

  const handleRescheduleSubmit = async (rescheduleData) => {
    try {
      const appointmentId = rescheduleData.appointmentId;
      await api.post(`/appointments/${appointmentId}/reschedule`, rescheduleData);
      toast.success('Appointment rescheduled successfully!');
      setSelectedAppointmentForReschedule(null);
      
      // Highlight and scroll to appointment
      setHighlightedAppointmentId(appointmentId);
      await fetchAppointments();
      
      setTimeout(() => {
        const element = document.getElementById(`appointment-${appointmentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => setHighlightedAppointmentId(null), 3000);
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule');
    }
  };

  const handleFollowUpSubmit = async (followUpData) => {
    try {
      const originalAppointmentId = followUpData.originalAppointmentId;
      const response = await api.post('/appointments', {
        patient_id: followUpData.patient_id,
        service_id: followUpData.service_id,
        scheduled_date: followUpData.scheduled_date,
        scheduled_time: followUpData.scheduled_time
      });
      const followUpAppointmentId = response.data.id;
      await api.put(`/appointments/${originalAppointmentId}`, {
        follow_up_appointment_id: followUpAppointmentId
      });
      toast.success('Follow-up appointment created successfully!');
      setSelectedAppointmentForFollowUp(null);
      
      // Highlight and scroll to original appointment
      setHighlightedAppointmentId(originalAppointmentId);
      await fetchAppointments();
      
      setTimeout(() => {
        const element = document.getElementById(`appointment-${originalAppointmentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => setHighlightedAppointmentId(null), 3000);
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create follow-up');
    }
  };

  const handleNotesSubmit = async (notesData) => {
    try {
      const appointmentId = notesData.appointmentId;
      await api.put(`/appointments/${appointmentId}`, {
        remarks: notesData.remarks
      });
      toast.success('Notes saved successfully!');
      setSelectedAppointmentForNotes(null);
      
      // Highlight and scroll to appointment
      setHighlightedAppointmentId(appointmentId);
      await fetchAppointments();
      
      setTimeout(() => {
        const element = document.getElementById(`appointment-${appointmentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => setHighlightedAppointmentId(null), 3000);
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    }
  };

  const handleViewLinkedAppointment = async (linkedAppointmentId) => {
    try {
      // Fetch the linked appointment
      const response = await api.get(`/appointments/${linkedAppointmentId}`);
      const linkedAppointment = response.data;
      
      // Change date to the linked appointment's date
      const linkedDate = new Date(linkedAppointment.scheduled_date);
      setSelectedDate(linkedDate);
      
      // Highlight the appointment after a short delay
      setTimeout(() => {
        setHighlightedAppointmentId(linkedAppointmentId);
        const element = document.getElementById(`appointment-${linkedAppointmentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        // Remove highlight after 3 seconds
        setTimeout(() => setHighlightedAppointmentId(null), 3000);
      }, 500);
      
      toast.success('Navigated to linked appointment');
    } catch (error) {
      toast.error('Failed to find linked appointment');
    }
  };

  // Filter appointments
  const timeBasedAppointments = appointments.filter(apt => 
    apt.service_type === 'time_based' &&
    (filterStatus === 'all' || apt.status === filterStatus) &&
    (filterService === 'all' || apt.service_id === filterService) &&
    (searchQuery === '' || 
      apt.patient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      apt.patient_phone.includes(searchQuery)
    )
  );

  const queueBasedAppointments = appointments
    .filter(apt => 
      apt.service_type === 'queue_based' &&
      (filterStatus === 'all' || apt.status === filterStatus) &&
      (filterService === 'all' || apt.service_id === filterService) &&
      (searchQuery === '' || 
        apt.patient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        apt.patient_phone.includes(searchQuery)
      )
    )
    .sort((a, b) => {
      // Priority 1: Appointments with tokens (sorted by MISHA pattern: M1,I1,S1,H1,A1,M2,I2,S2...)
      const aHasToken = a.token_number ? 1 : 0;
      const bHasToken = b.token_number ? 1 : 0;
      
      if (aHasToken !== bHasToken) {
        return bHasToken - aHasToken; // Appointments with tokens come first
      }
      
      // If both have tokens, sort by MISHA pattern (number first, then letter)
      if (aHasToken && bHasToken) {
        // MISHA pattern: M=0, I=1, S=2, H=3, A=4
        const mishaOrder = { 'M': 0, 'I': 1, 'S': 2, 'H': 3, 'A': 4 };
        
        // Extract letter and number from token (e.g., "M1" -> letter='M', num=1)
        const aLetter = a.token_number.charAt(0).toUpperCase();
        const bLetter = b.token_number.charAt(0).toUpperCase();
        const aNum = parseInt(a.token_number.substring(1)) || 0;
        const bNum = parseInt(b.token_number.substring(1)) || 0;
        
        // Sort by number first
        if (aNum !== bNum) {
          return aNum - bNum;
        }
        
        // If same number, sort by MISHA letter order
        const aLetterOrder = mishaOrder[aLetter] ?? 99;
        const bLetterOrder = mishaOrder[bLetter] ?? 99;
        return aLetterOrder - bLetterOrder;
      }
      
      // Priority 2: Among appointments without tokens, scheduled comes before rescheduled
      const statusOrder = { 'scheduled': 1, 'confirmed': 2, 'rescheduled': 3 };
      const aOrder = statusOrder[a.status] || 99;
      const bOrder = statusOrder[b.status] || 99;
      
      return aOrder - bOrder;
    });

  return (
    <Layout user={user} onLogout={onLogout}>
      {/* View Toggle Button - Admin Only */}
      {user?.role === 'Admin' && (
        <div className="absolute top-4 left-4 z-[60]">
          <Button
            onClick={toggleMobileView}
            size="sm"
            variant="outline"
            className="bg-white shadow-lg border-2 hover:bg-gray-50"
            title={isMobileView ? "Switch to Desktop View" : "Switch to Mobile View"}
          >
            {isMobileView ? (
              <><Monitor className="w-4 h-4 mr-2" /> Desktop</>
            ) : (
              <><Smartphone className="w-4 h-4 mr-2" /> Mobile</>
            )}
          </Button>
        </div>
      )}

      {/* Desktop View */}
      {!isMobileView && (
        <div className="flex h-full bg-gray-50">
          {/* Column 1: Calendar */}
          <div className="w-80 border-r border-gray-200 bg-white flex flex-col">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Calendar</h2>
            <p className="text-sm text-gray-500">Select a date</p>
          </div>
          
          <div className="p-6 flex-1 overflow-auto">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                // Prevent double-click issues and rapid successive calls
                if (!date || isProcessingDateChange.current) {
                  return; // Ignore if no date or already processing
                }
                
                // Create a proper local date without timezone complications
                const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                
                // Ignore if clicking the same date that's already selected
                if (selectedDate && selectedDate.getTime() === localDate.getTime()) {
                  return;
                }
                
                // Clear any pending timeout
                if (calendarClickTimeout.current) {
                  clearTimeout(calendarClickTimeout.current);
                }
                
                // Set processing flag
                isProcessingDateChange.current = true;
                
                // Update the date
                calendarClickTimeout.current = setTimeout(() => {
                  try {
                    setSelectedDate(localDate);
                  } catch (error) {
                    console.error('Error selecting date:', error);
                  } finally {
                    // Reset processing flag after a delay
                    setTimeout(() => {
                      isProcessingDateChange.current = false;
                    }, 500);
                  }
                }, 100);
              }}
              className="rounded-xl border shadow-sm"
            />

            <div className="mt-6">
              <Button 
                data-testid="create-appointment-button"
                onClick={() => setShowCreateModal(true)}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 font-semibold"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Appointment
              </Button>
            </div>

            {canSeeRegistrations && <NewRegistrationsBox onBook={openBookingFor} refreshKey={registrationsKey} />}

            <div className="mt-6 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
              <h3 className="font-semibold text-purple-900 mb-3">Appointment Stats</h3>
              <div className="space-y-2.5 text-sm">
                {/* Total */}
                <div className="flex justify-between items-center pb-2 border-b border-purple-200">
                  <span className="text-gray-700 font-medium">Total Today:</span>
                  <span className="font-bold text-purple-700 text-lg">{appointments.length}</span>
                </div>
                
                {/* Status Counts */}
                <div className="space-y-1.5 pt-1">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      Scheduled:
                    </span>
                    <span className="font-semibold text-blue-700">
                      {appointments.filter(a => a.status === 'scheduled').length}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Checked In:
                    </span>
                    <span className="font-semibold text-green-700">
                      {appointments.filter(a => a.status === 'checked_in').length}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                      Checked Out:
                    </span>
                    <span className="font-semibold text-emerald-700">
                      {appointments.filter(a => a.status === 'checked_out').length}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center gap-2">
                      <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                      Follow-ups Created:
                    </span>
                    <span className="font-semibold text-teal-700" data-testid="stat-followups-created">
                      {appointments.filter(a => a.follow_up_appointment_id).length}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                      Rescheduled:
                    </span>
                    <span className="font-semibold text-amber-700">
                      {appointments.filter(a => a.status === 'rescheduled' || a.status === 'forwarded').length}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      Cancelled:
                    </span>
                    <span className="font-semibold text-red-700">
                      {appointments.filter(a => a.status === 'cancelled').length}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center gap-2">
                      <span className="w-2 h-2 bg-rose-500 rounded-full"></span>
                      No Show:
                    </span>
                    <span className="font-semibold text-rose-700">
                      {appointments.filter(a => a.status === 'no_show').length}
                    </span>
                  </div>
                </div>
                
                {/* Type Breakdown */}
                <div className="pt-2 mt-2 border-t border-purple-200 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-xs">Time-based:</span>
                    <span className="font-semibold text-sky-600 text-xs">{timeBasedAppointments.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-xs">Queue-based:</span>
                    <span className="font-semibold text-emerald-600 text-xs">{queueBasedAppointments.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Column 2: Time-Based Appointments */}
        <div className="flex-1 border-r border-gray-200 bg-gray-50 flex flex-col">
          <div className="p-6 border-b border-gray-200 bg-white">
            <div className="flex items-center mb-3">
              <div className="h-1 w-12 bg-gradient-to-r from-sky-400 to-sky-600 rounded-full mr-3"></div>
              <h2 className="text-xl font-bold text-gray-900">Time-Based</h2>
              <span className="ml-3 px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-medium">
                {timeBasedAppointments.length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {selectedDate.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
              <div className="flex gap-3 text-xs">
                <span className="text-green-600 font-medium">
                  ✓ {timeBasedAppointments.filter(a => a.status === 'checked_in').length} In
                </span>
                <span className="text-purple-600 font-medium">
                  ✓ {timeBasedAppointments.filter(a => a.status === 'checked_out').length} Out
                </span>
              </div>
            </div>
            
            {/* Search and Filters */}
            <div className="flex gap-3 mt-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  data-testid="search-appointments-input"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-10 rounded-lg text-sm"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36 h-10 rounded-lg text-sm" data-testid="filter-status-select">
                  <Filter className="w-4 h-4 mr-1" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="checked_in">Checked In</SelectItem>
                  <SelectItem value="checked_out">Checked Out</SelectItem>
                  <SelectItem value="forwarded">Forwarded</SelectItem>
                  <SelectItem value="rescheduled">Rescheduled</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterService} onValueChange={setFilterService}>
                <SelectTrigger className="w-36 h-10 rounded-lg text-sm" data-testid="filter-service-select">
                  <SelectValue placeholder="Service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {services.map(service => (
                    <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex-1 overflow-auto p-4">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : timeBasedAppointments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
                <p className="text-gray-500 text-sm">No time-based appointments</p>
              </div>
            ) : (
              <div className="space-y-3">
                {timeBasedAppointments.map(appointment => (
                  <AppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    user={user}
                    onCheckIn={() => handleAction('checkin', appointment.id)}
                    onUndoCheckIn={() => handleAction('undocheckin', appointment.id)}
                    onCheckOut={() => handleAction('checkout', appointment.id)}
                    onCancel={() => handleAction('cancel', appointment.id)}
                    onConfirm={() => handleAction('confirm', appointment.id)}
                    onMarkPackage={() => handleAction('markpackage', appointment.id)}
                    onUndoPackage={() => handleAction('undopackage', appointment.id)}
                    onIssueToken={() => handleIssueToken(appointment)}
                    onNoShow={() => handleAction('noshow', appointment.id, { patient_id: appointment.patient_id })}
                    onReschedule={() => handleReschedule(appointment)}
                    onFollowUp={() => handleFollowUp(appointment)}
                    onAddNotes={() => handleAddNotes(appointment)}
                    onSendDirections={() => handleSendDirections(appointment.id)}
                    onViewPatient={() => handleViewPatient(appointment.patient_id)}
                    onViewLinkedAppointment={handleViewLinkedAppointment}
                    isHighlighted={highlightedAppointmentId === appointment.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Column 3: Queue-Based Appointments */}
        <div className="flex-1 bg-gray-50 flex flex-col">
          <div className="p-6 border-b border-gray-200 bg-white">
            <div className="flex items-center mb-3">
              <div className="h-1 w-12 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full mr-3"></div>
              <h2 className="text-xl font-bold text-gray-900">Queue-Based</h2>
              <span className="ml-3 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                {queueBasedAppointments.length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {selectedDate.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
              <div className="flex gap-3 text-xs">
                <span className="text-green-600 font-medium">
                  ✓ {queueBasedAppointments.filter(a => a.status === 'checked_in').length} In
                </span>
                <span className="text-purple-600 font-medium">
                  ✓ {queueBasedAppointments.filter(a => a.status === 'checked_out').length} Out
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-auto p-4">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : queueBasedAppointments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
                <p className="text-gray-500 text-sm">No queue-based appointments</p>
              </div>
            ) : (
              <div className="space-y-3">
                {queueBasedAppointments.map(appointment => (
                  <AppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    user={user}
                    onCheckIn={() => handleAction('checkin', appointment.id)}
                    onUndoCheckIn={() => handleAction('undocheckin', appointment.id)}
                    onCheckOut={() => handleAction('checkout', appointment.id)}
                    onCancel={() => handleAction('cancel', appointment.id)}
                    onConfirm={() => handleAction('confirm', appointment.id)}
                    onMarkPackage={() => handleAction('markpackage', appointment.id)}
                    onUndoPackage={() => handleAction('undopackage', appointment.id)}
                    onIssueToken={() => handleIssueToken(appointment)}
                    onNoShow={() => handleAction('noshow', appointment.id, { patient_id: appointment.patient_id })}
                    onReschedule={() => handleReschedule(appointment)}
                    onFollowUp={() => handleFollowUp(appointment)}
                    onAddNotes={() => handleAddNotes(appointment)}
                    onSendDirections={() => handleSendDirections(appointment.id)}
                    onViewPatient={() => handleViewPatient(appointment.patient_id)}
                    onViewLinkedAppointment={handleViewLinkedAppointment}
                    isHighlighted={highlightedAppointmentId === appointment.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Mobile View - Full Screen Overlay */}
      {isMobileView && (
        <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col overflow-hidden" style={{marginLeft: 0, marginTop: 0}}>
          {/* Mobile Header with Date - More Compact */}
          <div className="bg-white border-b border-gray-200 pl-24 pr-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-bold text-gray-900 leading-none">Dashboard</h2>
                {/* Date navigator: prev / date-picker / next */}
                <div className="mt-1 flex items-center gap-1" data-testid="mobile-date-toggle">
                  <button
                    onClick={() => {
                      const d = new Date(selectedDate);
                      d.setDate(d.getDate() - 1);
                      setSelectedDate(d);
                    }}
                    aria-label="Previous day"
                    className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
                    data-testid="mobile-date-prev"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <label className="relative inline-flex items-center">
                    <span className="text-xs text-gray-800 font-medium bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 rounded-md px-2 py-0.5 cursor-pointer select-none" data-testid="mobile-date-label">
                      📅 {selectedDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                    <input
                      type="date"
                      value={`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        const [y, m, d] = e.target.value.split('-').map(Number);
                        setSelectedDate(new Date(y, m - 1, d));
                      }}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      data-testid="mobile-date-picker"
                    />
                  </label>
                  <button
                    onClick={() => {
                      const d = new Date(selectedDate);
                      d.setDate(d.getDate() + 1);
                      setSelectedDate(d);
                    }}
                    aria-label="Next day"
                    className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
                    data-testid="mobile-date-next"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                  {selectedDate.toDateString() !== new Date().toDateString() && (
                    <button
                      onClick={() => setSelectedDate(new Date())}
                      className="text-[10px] font-semibold text-purple-700 hover:text-purple-900 ml-1"
                      data-testid="mobile-date-today"
                    >
                      Today
                    </button>
                  )}
                </div>
              </div>
              <Button 
                onClick={() => setShowCreateModal(true)}
                size="sm"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-xs h-8 px-2 flex-shrink-0"
              >
                <Plus className="w-3 h-3 mr-1" />
                New
              </Button>
            </div>
          </div>

          {canSeeRegistrations && <NewRegistrationsBox onBook={openBookingFor} refreshKey={registrationsKey} compact />}

          {/* Compact Stats */}
          <div className="bg-white border-b border-gray-200 px-2 py-2">
            <div className="grid grid-cols-5 gap-1 text-center">
              <div className="px-1">
                <div className="font-bold text-base text-blue-600">{appointments.filter(a => a.status === 'scheduled').length}</div>
                <div className="text-[10px] text-gray-500">Scheduled</div>
              </div>
              <div className="px-1">
                <div className="font-bold text-base text-green-600">{appointments.filter(a => a.status === 'checked_in').length}</div>
                <div className="text-[10px] text-gray-500">In</div>
              </div>
              <div className="px-1">
                <div className="font-bold text-base text-purple-600">{appointments.filter(a => a.status === 'checked_out').length}</div>
                <div className="text-[10px] text-gray-500">Out</div>
              </div>
              <div className="px-1">
                <div className="font-bold text-base text-teal-600" data-testid="stat-mobile-followups">{appointments.filter(a => a.follow_up_appointment_id).length}</div>
                <div className="text-[10px] text-gray-500">Followups</div>
              </div>
              <div className="px-1">
                <div className="font-bold text-base text-gray-700">{appointments.length}</div>
                <div className="text-[10px] text-gray-500">Total</div>
              </div>
            </div>
          </div>

          {/* Payment + Activity Summary Card - Admin Only */}
          {user?.role === 'Admin' && (
            <div className="relative mx-2 mt-2 rounded-2xl overflow-hidden shadow-lg" data-testid="admin-mobile-top-card">
              {/* Multi-stop vibrant gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-fuchsia-600 to-orange-500" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.25),transparent_55%)]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(0,0,0,0.25),transparent_60%)]" />

              <div className="relative p-3 text-white">
                {/* Header row: Collection total */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center">
                      <IndianRupee className="w-4 h-4" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wide opacity-95">Today&apos;s Collection</span>
                  </div>
                  <span className="text-xl font-extrabold drop-shadow-sm">₹{paymentSummary.grandTotal.toLocaleString()}</span>
                </div>

                {/* Payment method chips */}
                {Object.keys(paymentSummary.totalsByMethod).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {Object.entries(paymentSummary.totalsByMethod).map(([method, amount]) => (
                      <div
                        key={method}
                        className="bg-white/25 backdrop-blur-sm border border-white/30 rounded-full px-2.5 py-1 flex items-center gap-1"
                        data-testid={`pay-method-${method}`}
                      >
                        <CreditCard className="w-3 h-3" />
                        <span className="text-[10px] font-medium">{method}:</span>
                        <span className="text-[11px] font-bold">₹{amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pharmacy total sale — single number, no breakup */}
                <div
                  className="mt-1 flex items-center justify-between bg-white/15 backdrop-blur-sm rounded-lg px-2.5 py-1.5 border border-white/20"
                  data-testid="pharmacy-daily-total"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">💊</span>
                    <span className="text-[11px] font-semibold uppercase tracking-wide opacity-95">Pharmacy Sale</span>
                  </div>
                  <span className="text-sm font-extrabold drop-shadow-sm">₹{Number(pharmacyDailyTotal.total).toLocaleString('en-IN')}</span>
                </div>

                {/* Divider label */}
                <div className="flex items-center gap-2 mt-2 mb-1.5">
                  <div className="h-px flex-1 bg-white/25" />
                  <span className="text-[9px] font-bold tracking-widest opacity-90 uppercase">Today&apos;s Activity</span>
                  <div className="h-px flex-1 bg-white/25" />
                </div>

                {/* Activity chips grid: Packages Created / Redeemed / Edited + Job Cards */}
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => setPkgDrill('created')}
                    data-testid="pkg-chip-created"
                    className="group text-left bg-gradient-to-br from-emerald-400/95 to-teal-500/95 hover:from-emerald-400 hover:to-teal-500 rounded-xl p-2 flex items-center gap-2 shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/30 flex items-center justify-center flex-shrink-0">
                      <PackageIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] font-semibold uppercase tracking-wide opacity-95">Packages Created</div>
                      <div className="text-lg font-extrabold leading-none">{pkgSummary.created.count}</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setPkgDrill('redeemed')}
                    data-testid="pkg-chip-redeemed"
                    className="group text-left bg-gradient-to-br from-sky-400/95 to-blue-600/95 hover:from-sky-400 hover:to-blue-600 rounded-xl p-2 flex items-center gap-2 shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/30 flex items-center justify-center flex-shrink-0">
                      <RefreshCcw className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] font-semibold uppercase tracking-wide opacity-95">Packages Redeemed</div>
                      <div className="text-lg font-extrabold leading-none">{pkgSummary.redeemed.count}</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setPkgDrill('edited')}
                    data-testid="pkg-chip-edited"
                    className="group text-left bg-gradient-to-br from-amber-400/95 to-orange-500/95 hover:from-amber-400 hover:to-orange-500 rounded-xl p-2 flex items-center gap-2 shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/30 flex items-center justify-center flex-shrink-0">
                      <PencilLine className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] font-semibold uppercase tracking-wide opacity-95">Packages Edited</div>
                      <div className="text-lg font-extrabold leading-none">{pkgSummary.edited.count}</div>
                    </div>
                  </button>

                  <button
                    onClick={() => setJcDrill(true)}
                    data-testid="jc-chip"
                    className="group text-left bg-gradient-to-br from-rose-500/95 to-pink-600/95 hover:from-rose-500 hover:to-pink-600 rounded-xl p-2 flex items-center gap-2 shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/30 flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] font-semibold uppercase tracking-wide opacity-95">Job Cards</div>
                      <div className="text-lg font-extrabold leading-none">{jobCards.length}</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tabs for Time-Based / Queue-Based */}
          <div className="bg-white border-b border-gray-200 flex">
            <button
              onClick={() => setMobileActiveTab('time-based')}
              className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors ${
                mobileActiveTab === 'time-based'
                  ? 'border-sky-600 text-sky-600'
                  : 'border-transparent text-gray-500'
              }`}
            >
              Time ({timeBasedAppointments.length})
            </button>
            <button
              onClick={() => setMobileActiveTab('queue-based')}
              className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors ${
                mobileActiveTab === 'queue-based'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500'
              }`}
            >
              Queue ({queueBasedAppointments.length})
            </button>
          </div>

          {/* Search */}
          <div className="bg-white px-2 py-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-7 h-8 text-xs"
              />
            </div>
          </div>

          {/* Appointments List */}
          <div className="flex-1 overflow-auto px-2 py-2">
            {mobileActiveTab === 'time-based' ? (
              timeBasedAppointments.length === 0 ? (
                <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-gray-200">
                  <p className="text-gray-500 text-xs">No time-based appointments</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {timeBasedAppointments.map(appointment => (
                    <div 
                      key={appointment.id} 
                      id={`appointment-${appointment.id}`}
                      data-appointment-id={appointment.id}
                      className={`bg-white rounded-lg border-2 p-3 text-xs ${
                        highlightedAppointmentId === appointment.id 
                          ? 'ring-2 ring-purple-500 border-purple-300' 
                          : appointment.is_paid 
                            ? 'border-green-200' 
                            : 'border-amber-200'
                      }`}
                    >
                      {/* Reference Number */}
                      {appointment.ref_number && (
                        <div className="mb-1">
                          <span className="text-[9px] font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                            {appointment.ref_number}
                          </span>
                        </div>
                      )}
                      {/* Header with Patient Info & Status */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-1 flex-wrap">
                            <h3 className="font-bold text-sm text-gray-900">{appointment.patient_name}</h3>
                            {appointment.patient_tags && appointment.patient_tags.map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[8px]">
                                {tag}
                              </span>
                            ))}
                          </div>
                          <p className="text-[10px] text-gray-500">{appointment.patient_phone}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                            appointment.status === 'checked_in' ? 'bg-green-100 text-green-700' :
                            appointment.status === 'checked_out' ? 'bg-purple-100 text-purple-700' :
                            appointment.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                            appointment.status === 'confirmed' ? 'bg-teal-100 text-teal-700' :
                            appointment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            appointment.status === 'no_show' ? 'bg-rose-100 text-rose-700' :
                            appointment.status === 'forwarded' ? 'bg-amber-100 text-amber-700' :
                            appointment.status === 'rescheduled' ? 'bg-indigo-100 text-indigo-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {appointment.status.replace('_', ' ')}
                          </span>
                          {/* Payment Status Badge */}
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            appointment.is_paid 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {appointment.is_paid 
                              ? `✓ ${user?.role === 'Admin' && appointment.payment_amount > 0 ? '₹' + appointment.payment_amount.toLocaleString() : 'Paid'}` 
                              : '₹ Unpaid'}
                          </span>
                          {appointment.is_package && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">
                              Package
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Service & Time Info */}
                      <div className="bg-gray-50 rounded p-2 mb-2 space-y-1">
                        <div className="flex justify-between text-[10px]">
                          <span className="text-gray-500">Service:</span>
                          <span className="font-medium text-gray-900">{appointment.service_name}</span>
                        </div>
                        <div className="flex justify-between text-[10px]">
                          <span className="text-gray-500">Scheduled:</span>
                          <span className="font-medium text-gray-900">{appointment.scheduled_time}</span>
                        </div>
                        {appointment.check_in_time && (
                          <div className="flex justify-between text-[10px]">
                            <span className="text-gray-500">Check-in:</span>
                            <span className="font-medium text-emerald-700">
                              {new Date(appointment.check_in_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                        {appointment.lateness_minutes > 0 && (
                          <div className="flex justify-between text-[10px]">
                            <span className="text-gray-500">Lateness:</span>
                            <span className="font-medium text-amber-600">{appointment.lateness_minutes} min late</span>
                          </div>
                        )}
                        {appointment.check_out_time && (
                          <div className="flex justify-between text-[10px]">
                            <span className="text-gray-500">Check-out:</span>
                            <span className="font-medium text-purple-700">
                              {new Date(appointment.check_out_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                        {appointment.exposure_time_minutes && (
                          <div className="flex justify-between text-[10px]">
                            <span className="text-gray-500">Duration:</span>
                            <span className="font-medium text-purple-700">{Math.round(appointment.exposure_time_minutes)} min</span>
                          </div>
                        )}
                      </div>

                      {/* Job Card Status */}
                      {appointment.job_card_status && (
                        <div className={`rounded p-2 mb-2 text-[10px] ${
                          appointment.job_card_status === 'completed' ? 'bg-green-50 border border-green-200' :
                          appointment.job_card_status === 'in_progress' ? 'bg-blue-50 border border-blue-200' :
                          appointment.job_card_status === 'cancelled' ? 'bg-red-50 border border-red-200' :
                          'bg-orange-50 border border-orange-200'
                        }`}>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Job Card:</span>
                            <div className="flex items-center gap-2">
                              {user?.role === 'Admin' && appointment.payment_amount > 0 && (
                                <span className="font-bold text-green-700">₹{appointment.payment_amount.toLocaleString()}</span>
                              )}
                              <span className={`font-semibold ${
                                appointment.job_card_status === 'completed' ? 'text-green-700' :
                                appointment.job_card_status === 'in_progress' ? 'text-blue-700' :
                                appointment.job_card_status === 'cancelled' ? 'text-red-700' :
                                'text-orange-700'
                              }`}>
                                {appointment.job_card_status.replace('_', ' ')} {appointment.job_card_progress && `(${appointment.job_card_progress})`}
                              </span>
                            </div>
                          </div>
                          {appointment.job_card_therapists && appointment.job_card_therapists.length > 0 && (
                            <div className="mt-1 pt-1 border-t border-gray-200 text-gray-600">
                              👤 {appointment.job_card_therapists.join(', ')}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Linked Appointments Info */}
                      {appointment.status === 'forwarded' && appointment.rescheduled_to_appointment_id && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-[10px]">
                          <span className="text-amber-800">↪ Forwarded to new appointment</span>
                        </div>
                      )}
                      {appointment.status === 'rescheduled' && appointment.original_appointment_id && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded p-2 mb-2 text-[10px]">
                          <span className="text-indigo-800">↩ Rescheduled from original</span>
                        </div>
                      )}
                      {appointment.follow_up_appointment_id && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 mb-2 text-[10px]">
                          <span className="text-emerald-800">✓ Follow-up created</span>
                        </div>
                      )}

                      {/* Notes */}
                      {appointment.remarks && (
                        <div className="bg-blue-50 border-l-2 border-blue-400 rounded p-2 mb-2 text-[10px]">
                          <div className="font-semibold text-blue-800 mb-0.5">Notes:</div>
                          <p className="text-blue-900 line-clamp-2">{appointment.remarks}</p>
                        </div>
                      )}

                      {/* Last Updated Info */}
                      {(appointment.last_updated_by_user_name || appointment.created_by_user_name) && (
                        <div className="text-[9px] text-gray-400 mb-2">
                          {appointment.last_updated_by_user_name
                            ? `Updated by: ${appointment.last_updated_by_user_name.split(' ')[0]}`
                            : `Created by: ${appointment.created_by_user_name.split(' ')[0]}`
                          }
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="grid grid-cols-3 gap-1">
                        {(appointment.status === 'scheduled' || appointment.status === 'confirmed' || appointment.status === 'rescheduled') && (
                          <button onClick={() => handleAction('checkin', appointment.id)} className="bg-green-600 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Check In
                          </button>
                        )}
                        {appointment.status === 'checked_in' && (
                          <button onClick={() => handleAction('checkout', appointment.id)} className="bg-purple-600 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Check Out
                          </button>
                        )}
                        {(appointment.status === 'scheduled' || appointment.status === 'rescheduled') && (
                          <button onClick={() => handleAction('confirm', appointment.id)} className="bg-teal-600 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Confirm
                          </button>
                        )}
                        <button onClick={() => handleReschedule(appointment)} className="bg-indigo-500 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                          Reschedule
                        </button>
                        <button onClick={() => handleAddNotes(appointment)} className="bg-blue-500 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                          {appointment.remarks ? 'Edit Notes' : 'Notes'}
                        </button>
                        <button onClick={() => handleViewPatient(appointment.patient_id)} className="bg-gray-500 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                          Profile
                        </button>
                        {(appointment.status === 'checked_in' || appointment.status === 'checked_out') && !appointment.follow_up_appointment_id && (
                          <button onClick={() => handleFollowUp(appointment)} className="bg-emerald-500 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Follow-up
                          </button>
                        )}
                        {appointment.status !== 'cancelled' && appointment.status !== 'checked_in' && appointment.status !== 'checked_out' && appointment.status !== 'no_show' && (
                          <button onClick={() => handleAction('cancel', appointment.id)} className="bg-red-500 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              queueBasedAppointments.length === 0 ? (
                <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-gray-200">
                  <p className="text-gray-500 text-xs">No queue-based appointments</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {queueBasedAppointments.map(appointment => (
                    <div 
                      key={appointment.id} 
                      id={`appointment-${appointment.id}`}
                      data-appointment-id={appointment.id}
                      className={`bg-white rounded-lg border-2 p-3 text-xs ${
                        highlightedAppointmentId === appointment.id 
                          ? 'ring-2 ring-purple-500 border-purple-300' 
                          : appointment.is_paid 
                            ? 'border-green-200' 
                            : 'border-amber-200'
                      }`}
                    >
                      {/* Reference Number */}
                      {appointment.ref_number && (
                        <div className="mb-1">
                          <span className="text-[9px] font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                            {appointment.ref_number}
                          </span>
                        </div>
                      )}
                      {/* Header with Patient Info & Status */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-1 flex-wrap">
                            <h3 className="font-bold text-sm text-gray-900">{appointment.patient_name}</h3>
                            {appointment.patient_tags && appointment.patient_tags.map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[8px]">
                                {tag}
                              </span>
                            ))}
                          </div>
                          <p className="text-[10px] text-gray-500">{appointment.patient_phone}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                            appointment.status === 'checked_in' ? 'bg-green-100 text-green-700' :
                            appointment.status === 'checked_out' ? 'bg-purple-100 text-purple-700' :
                            appointment.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                            appointment.status === 'confirmed' ? 'bg-teal-100 text-teal-700' :
                            appointment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            appointment.status === 'no_show' ? 'bg-rose-100 text-rose-700' :
                            appointment.status === 'forwarded' ? 'bg-amber-100 text-amber-700' :
                            appointment.status === 'rescheduled' ? 'bg-indigo-100 text-indigo-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {appointment.status.replace('_', ' ')}
                          </span>
                          {/* Payment Status Badge */}
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            appointment.is_paid 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {appointment.is_paid 
                              ? `✓ ${user?.role === 'Admin' && appointment.payment_amount > 0 ? '₹' + appointment.payment_amount.toLocaleString() : 'Paid'}` 
                              : '₹ Unpaid'}
                          </span>
                          {appointment.is_package && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">
                              Package
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Token Number - Prominent for Queue */}
                      {appointment.token_number && (
                        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded p-2 mb-2 flex items-center justify-between">
                          <span className="text-[10px] text-gray-600">Token:</span>
                          <span className="text-lg font-bold text-emerald-700 font-mono">{appointment.token_number}</span>
                        </div>
                      )}
                      {!appointment.token_number && appointment.status !== 'cancelled' && appointment.status !== 'checked_out' && appointment.status !== 'no_show' && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-center text-[10px] text-amber-700 font-medium">
                          Token not issued yet
                        </div>
                      )}
                      
                      {/* Service & Time Info */}
                      <div className="bg-gray-50 rounded p-2 mb-2 space-y-1">
                        <div className="flex justify-between text-[10px]">
                          <span className="text-gray-500">Service:</span>
                          <span className="font-medium text-gray-900">{appointment.service_name}</span>
                        </div>
                        {appointment.check_in_time && (
                          <div className="flex justify-between text-[10px]">
                            <span className="text-gray-500">Check-in:</span>
                            <span className="font-medium text-emerald-700">
                              {new Date(appointment.check_in_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                        {appointment.check_out_time && (
                          <div className="flex justify-between text-[10px]">
                            <span className="text-gray-500">Check-out:</span>
                            <span className="font-medium text-purple-700">
                              {new Date(appointment.check_out_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                        {appointment.exposure_time_minutes && (
                          <div className="flex justify-between text-[10px]">
                            <span className="text-gray-500">Duration:</span>
                            <span className="font-medium text-purple-700">{Math.round(appointment.exposure_time_minutes)} min</span>
                          </div>
                        )}
                      </div>

                      {/* Job Card Status */}
                      {appointment.job_card_status && (
                        <div className={`rounded p-2 mb-2 text-[10px] ${
                          appointment.job_card_status === 'completed' ? 'bg-green-50 border border-green-200' :
                          appointment.job_card_status === 'in_progress' ? 'bg-blue-50 border border-blue-200' :
                          appointment.job_card_status === 'cancelled' ? 'bg-red-50 border border-red-200' :
                          'bg-orange-50 border border-orange-200'
                        }`}>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Job Card:</span>
                            <div className="flex items-center gap-2">
                              {user?.role === 'Admin' && appointment.payment_amount > 0 && (
                                <span className="font-bold text-green-700">₹{appointment.payment_amount.toLocaleString()}</span>
                              )}
                              <span className={`font-semibold ${
                                appointment.job_card_status === 'completed' ? 'text-green-700' :
                                appointment.job_card_status === 'in_progress' ? 'text-blue-700' :
                                appointment.job_card_status === 'cancelled' ? 'text-red-700' :
                                'text-orange-700'
                              }`}>
                                {appointment.job_card_status.replace('_', ' ')} {appointment.job_card_progress && `(${appointment.job_card_progress})`}
                              </span>
                            </div>
                          </div>
                          {appointment.job_card_therapists && appointment.job_card_therapists.length > 0 && (
                            <div className="mt-1 pt-1 border-t border-gray-200 text-gray-600">
                              👤 {appointment.job_card_therapists.join(', ')}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Linked Appointments Info */}
                      {appointment.status === 'forwarded' && appointment.rescheduled_to_appointment_id && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-[10px]">
                          <span className="text-amber-800">↪ Forwarded to new appointment</span>
                        </div>
                      )}
                      {appointment.status === 'rescheduled' && appointment.original_appointment_id && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded p-2 mb-2 text-[10px]">
                          <span className="text-indigo-800">↩ Rescheduled from original</span>
                        </div>
                      )}
                      {appointment.follow_up_appointment_id && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 mb-2 text-[10px]">
                          <span className="text-emerald-800">✓ Follow-up created</span>
                        </div>
                      )}

                      {/* Notes */}
                      {appointment.remarks && (
                        <div className="bg-blue-50 border-l-2 border-blue-400 rounded p-2 mb-2 text-[10px]">
                          <div className="font-semibold text-blue-800 mb-0.5">Notes:</div>
                          <p className="text-blue-900 line-clamp-2">{appointment.remarks}</p>
                        </div>
                      )}

                      {/* Last Updated Info */}
                      {(appointment.last_updated_by_user_name || appointment.created_by_user_name) && (
                        <div className="text-[9px] text-gray-400 mb-2">
                          {appointment.last_updated_by_user_name
                            ? `Updated by: ${appointment.last_updated_by_user_name.split(' ')[0]}`
                            : `Created by: ${appointment.created_by_user_name.split(' ')[0]}`
                          }
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="grid grid-cols-3 gap-1">
                        {!appointment.token_number && appointment.service_type === 'queue_based' && appointment.status !== 'cancelled' && appointment.status !== 'checked_out' && appointment.status !== 'no_show' && (
                          <button onClick={() => handleIssueToken(appointment)} className="bg-indigo-600 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Issue Token
                          </button>
                        )}
                        {(appointment.status === 'scheduled' || appointment.status === 'confirmed' || appointment.status === 'rescheduled') && (
                          <button onClick={() => handleAction('checkin', appointment.id)} className="bg-green-600 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Check In
                          </button>
                        )}
                        {appointment.status === 'checked_in' && (
                          <button onClick={() => handleAction('checkout', appointment.id)} className="bg-purple-600 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Check Out
                          </button>
                        )}
                        {(appointment.status === 'scheduled' || appointment.status === 'rescheduled') && (
                          <button onClick={() => handleAction('confirm', appointment.id)} className="bg-teal-600 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Confirm
                          </button>
                        )}
                        <button onClick={() => handleReschedule(appointment)} className="bg-indigo-500 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                          Reschedule
                        </button>
                        <button onClick={() => handleAddNotes(appointment)} className="bg-blue-500 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                          {appointment.remarks ? 'Edit Notes' : 'Notes'}
                        </button>
                        <button onClick={() => handleViewPatient(appointment.patient_id)} className="bg-gray-500 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                          Profile
                        </button>
                        {(appointment.status === 'checked_in' || appointment.status === 'checked_out') && !appointment.follow_up_appointment_id && (
                          <button onClick={() => handleFollowUp(appointment)} className="bg-emerald-500 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Follow-up
                          </button>
                        )}
                        {appointment.status !== 'cancelled' && appointment.status !== 'checked_in' && appointment.status !== 'checked_out' && appointment.status !== 'no_show' && (
                          <button onClick={() => handleAction('cancel', appointment.id)} className="bg-red-500 text-white rounded px-2 py-1.5 text-[10px] font-medium">
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Package activity drill-down modal (mobile admin) */}
      {pkgDrill && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setPkgDrill(null)}
          data-testid="pkg-drill-modal"
        >
          <div
            className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 capitalize">
                {pkgDrill === 'created' && `Packages created — ${pkgSummary.created.count}`}
                {pkgDrill === 'redeemed' && `Redemptions — ${pkgSummary.redeemed.count}`}
                {pkgDrill === 'edited' && `Package edits — ${pkgSummary.edited.count}`}
              </h3>
              <button onClick={() => setPkgDrill(null)} className="p-1.5 hover:bg-gray-100 rounded-md">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="p-3 space-y-2">
              {pkgDrill === 'created' && pkgSummary.created.rows.map(p => (
                <div key={p.id} className="border border-purple-100 rounded-lg p-3 text-sm" data-testid={`pkg-drill-created-${p.ref_number}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Link to={`/packages?ref=${p.ref_number}`} onClick={() => setPkgDrill(null)}
                          className="font-mono text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded hover:bg-purple-100">
                      {p.ref_number}
                    </Link>
                    <span className="text-[11px] text-gray-500">{String(p.created_at || '').slice(11, 16)}</span>
                  </div>
                  <Link to={`/patients?id=${p.patient_id}`} onClick={() => setPkgDrill(null)}
                        className="font-semibold text-gray-900 hover:text-purple-700">
                    {p.patient_name || 'Unknown patient'}
                  </Link>
                  <p className="text-[11px] text-gray-500">
                    {p.patient_phone} · ₹{Number(p.total_cost).toLocaleString()} · paid ₹{Number(p.total_paid).toLocaleString()}
                    {Number(p.balance || 0) > 0 && <span className="text-orange-600"> · bal ₹{Number(p.balance).toLocaleString()}</span>}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">by {p.created_by_user_name}</p>
                </div>
              ))}
              {pkgDrill === 'redeemed' && pkgSummary.redeemed.rows.map(c => (
                <div key={c.id} className="border border-emerald-100 rounded-lg p-3 text-sm" data-testid={`pkg-drill-redeemed-${c.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Link to={`/packages?ref=${c.package_ref_number}`} onClick={() => setPkgDrill(null)}
                          className="font-mono text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded hover:bg-purple-100">
                      {c.package_ref_number}
                    </Link>
                    <span className="text-[11px] text-gray-500">{String(c.consumed_at || '').slice(11, 16)}</span>
                  </div>
                  <p className="font-semibold text-gray-900">{c.service_name}</p>
                  <p className="text-[11px] text-gray-500">
                    {c.appointment_ref_number || 'appt'} · by {c.consumed_by_user_name}
                  </p>
                </div>
              ))}
              {pkgDrill === 'edited' && pkgSummary.edited.rows.map(v => (
                <div key={v.id} className="border border-amber-100 rounded-lg p-3 text-sm" data-testid={`pkg-drill-edited-${v.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    {v.ref_number ? (
                      <Link to={`/packages?ref=${v.ref_number}`} onClick={() => setPkgDrill(null)}
                            className="font-mono text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded hover:bg-purple-100">
                        {v.ref_number}
                      </Link>
                    ) : (
                      <span className="font-mono text-xs text-gray-500">{(v.package_id || '').slice(0, 8)}</span>
                    )}
                    <span className="text-[11px] text-gray-500">{String(v.snapshot_at || '').slice(11, 16)}</span>
                  </div>
                  <p className="font-semibold text-gray-900">{v.patient_name || 'Patient'}</p>
                  {v.reason && <p className="text-[11px] text-gray-600 italic">&ldquo;{v.reason}&rdquo;</p>}
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    ₹{Number(v.previous?.total_cost || 0).toLocaleString()} → ₹{Number(v.new?.total_cost || 0).toLocaleString()}
                    · by {v.edited_by_user_name}
                  </p>
                </div>
              ))}
              {((pkgDrill === 'created' && pkgSummary.created.rows.length === 0) ||
                (pkgDrill === 'redeemed' && pkgSummary.redeemed.rows.length === 0) ||
                (pkgDrill === 'edited' && pkgSummary.edited.rows.length === 0)) && (
                <p className="text-center py-6 text-sm text-gray-400 italic">No activity today.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Job Cards drill-down modal (mobile admin) */}
      {jcDrill && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setJcDrill(false)}
          data-testid="jc-drill-modal"
        >
          <div
            className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-rose-500 to-pink-600 text-white flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                <h3 className="font-semibold">Job Cards — {jobCards.length}</h3>
              </div>
              <button onClick={() => setJcDrill(false)} className="p-1.5 hover:bg-white/20 rounded-md" data-testid="jc-drill-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Status summary bar */}
            <div className="grid grid-cols-4 gap-1 px-3 pt-3 pb-2 border-b border-gray-100">
              {[
                { key: 'pending', label: 'Waiting', color: 'text-gray-600 bg-gray-100', Icon: Clock },
                { key: 'in_progress', label: 'Active', color: 'text-blue-700 bg-blue-100', Icon: PlayCircle },
                { key: 'completed', label: 'Done', color: 'text-green-700 bg-green-100', Icon: CheckCircle2 },
                { key: 'cancelled', label: 'Cancelled', color: 'text-red-700 bg-red-100', Icon: X },
              ].map(({ key, label, color, Icon }) => {
                const count = jobCards.filter(jc => jc.status === key).length;
                return (
                  <div key={key} className={`rounded-lg px-1.5 py-1.5 text-center ${color}`} data-testid={`jc-status-${key}`}>
                    <Icon className="w-3.5 h-3.5 mx-auto mb-0.5" />
                    <div className="text-base font-bold leading-none">{count}</div>
                    <div className="text-[9px] font-medium mt-0.5">{label}</div>
                  </div>
                );
              })}
            </div>

            <div className="p-3 space-y-2">
              {jobCards.length === 0 && (
                <p className="text-center py-8 text-sm text-gray-400 italic">No job cards for this date.</p>
              )}
              {jobCards.map(jc => {
                const checklistDone = jc.pre_procedure_checklist?.filter(i => i.is_completed).length || 0;
                const checklistTotal = jc.pre_procedure_checklist?.length || 0;
                const stepsDone = jc.procedure_steps?.filter(s => s.is_completed).length || 0;
                const stepsTotal = jc.procedure_steps?.length || 0;
                const totalDone = checklistDone + stepsDone;
                const totalItems = checklistTotal + stepsTotal;
                const progressPct = totalItems > 0 ? (totalDone / totalItems) * 100 : 0;

                const statusBadge = {
                  pending: { label: 'Waiting', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
                  in_progress: { label: 'Active', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
                  completed: { label: 'Done', cls: 'bg-green-100 text-green-700 border-green-200' },
                  cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-700 border-red-200' },
                }[jc.status] || { label: jc.status, cls: 'bg-gray-100 text-gray-700 border-gray-200' };

                return (
                  <div
                    key={jc.id}
                    className="border border-rose-100 rounded-xl p-3 text-sm hover:shadow-sm transition"
                    data-testid={`jc-drill-item-${jc.id}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {jc.appointment_ref_number && (
                          <span className="font-mono text-[10px] text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded">
                            {jc.appointment_ref_number}
                          </span>
                        )}
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge.cls}`}>
                          {statusBadge.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-gray-500">{jc.scheduled_time || ''}</span>
                    </div>

                    <Link
                      to={`/job-cards?highlight=${jc.id}`}
                      onClick={() => setJcDrill(false)}
                      className="block font-semibold text-gray-900 hover:text-rose-700"
                    >
                      {jc.patient_name}
                    </Link>
                    <p className="text-[12px] text-gray-700 mt-0.5">{jc.service_name}</p>

                    {jc.assigned_therapist_names?.length > 0 && (
                      <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {jc.assigned_therapist_names.join(', ')}
                      </p>
                    )}

                    {totalItems > 0 && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                          <span>Progress</span>
                          <span className="font-semibold">{totalDone}/{totalItems}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${jc.status === 'completed' ? 'bg-green-500' : 'bg-gradient-to-r from-rose-400 to-pink-500'}`}
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                        <div className="flex gap-3 mt-1 text-[10px] text-gray-500">
                          {checklistTotal > 0 && <span>Checklist: {checklistDone}/{checklistTotal}</span>}
                          {stepsTotal > 0 && <span>Steps: {stepsDone}/{stepsTotal}</span>}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-500">
                      <span>
                        {jc.is_paid ? (
                          <span className="text-green-700 font-semibold">Paid ₹{Number(jc.payment_amount || 0).toLocaleString()}</span>
                        ) : (
                          <span className="text-orange-700 font-semibold">Unpaid</span>
                        )}
                      </span>
                      {jc.notes && <span className="italic text-gray-400 truncate ml-2">&ldquo;{jc.notes.slice(0, 40)}&rdquo;</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Recent Feedback Scores — internal rating gate (Admin + Front-Desk) */}
      <RecentFeedbackScoresSection user={user} />

      {/* Modals */}
      {showCreateModal && (
        <CreateAppointmentModal
          isOpen={showCreateModal}
          onClose={() => { setShowCreateModal(false); setBookPatient(null); }}
          onSuccess={fetchAppointments}
          onCreate={handleCreateAppointment}
          selectedDate={selectedDate}
          user={user}
          initialPatient={bookPatient}
        />
      )}

      {selectedPatient && (
        <PatientProfileModal
          isOpen={!!selectedPatient}
          onClose={() => setSelectedPatient(null)}
          patientId={selectedPatient}
          user={user}
        />
      )}

      {selectedAppointmentForReschedule && (
        <RescheduleModal
          isOpen={!!selectedAppointmentForReschedule}
          onClose={() => setSelectedAppointmentForReschedule(null)}
          appointment={selectedAppointmentForReschedule}
          onReschedule={handleRescheduleSubmit}
          user={user}
        />
      )}

      {selectedAppointmentForFollowUp && (
        <FollowUpModal
          isOpen={!!selectedAppointmentForFollowUp}
          onClose={() => setSelectedAppointmentForFollowUp(null)}
          appointment={selectedAppointmentForFollowUp}
          onFollowUp={handleFollowUpSubmit}
          user={user}
        />
      )}

      {selectedAppointmentForNotes && (
        <NotesModal
          isOpen={!!selectedAppointmentForNotes}
          onClose={() => setSelectedAppointmentForNotes(null)}
          appointment={selectedAppointmentForNotes}
          onNotes={handleNotesSubmit}
          user={user}
        />
      )}

      {selectedAppointmentForIssueToken && (
        <IssueTokenModal
          isOpen={!!selectedAppointmentForIssueToken}
          onClose={() => setSelectedAppointmentForIssueToken(null)}
          appointment={selectedAppointmentForIssueToken}
          onIssueToken={handleIssueTokenSubmit}
        />
      )}

      {/* Duplicate Appointment Confirmation Dialog */}
      <Dialog open={!!duplicateAppointmentData} onOpenChange={(open) => !open && setDuplicateAppointmentData(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Duplicate Appointment Detected
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-3">
                <p className="text-gray-700 font-medium">
                  {duplicateAppointmentData?.message}
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-semibold text-amber-900">Existing Appointment Details:</p>
                  {duplicateAppointmentData?.existingTime && duplicateAppointmentData.existingTime !== 'Not specified' && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-amber-700">🕐 Time:</span>
                      <span className="font-medium text-amber-900">{duplicateAppointmentData.existingTime}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-amber-700">📋 Status:</span>
                    <span className="font-medium text-amber-900 capitalize">{duplicateAppointmentData?.existingStatus?.replace('_', ' ')}</span>
                  </div>
                </div>

                <p className="text-sm text-gray-600 pt-2">
                  Would you like to create another appointment for the same service anyway?
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setDuplicateAppointmentData(null)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleCreateAppointment(duplicateAppointmentData.originalData, true);
                setDuplicateAppointmentData(null);
              }}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              Yes, Create Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Dashboard;
